import {
  PM_READ,
  PM_WRITE,
  PM_UPLOAD_ADD,
  PM_UPLOAD_DISABLE,
  UM_PM_GRANT_READ,
  UM_PM_GRANT_WRITE,
  SUPER_MANAGER,
  SUPER_MANAGER_READ_ONLY
} from '@/constants/permissions'

/**
 * Permissions that open the Priority Manager module.
 *
 * Mirrors the casbin rows for `/user_api/:api_version/tracking/uap/*` in the
 * backend's `access_control_config/policy.csv`. Keep the two in step: a
 * permission listed here without a matching policy row renders a page whose
 * requests 403.
 */
const PRIORITY_MANAGER_PERMISSIONS = [
  SUPER_MANAGER,
  SUPER_MANAGER_READ_ONLY,
  PM_READ,
  PM_WRITE,
  PM_UPLOAD_ADD,
  PM_UPLOAD_DISABLE,
  UM_PM_GRANT_READ,
  UM_PM_GRANT_WRITE
]

/**
 * Whether a user may reach the Priority Manager module.
 *
 * KPT-1049: `Navigation.vue` (nav tab) and `PriorityManager.vue` (view guard)
 * each carried their own copy of this rule and the copies drifted —
 * `Navigation` allow-listed `UM_user_acceptance` and the view guard did not, so
 * such a user saw the tab and was bounced on arrival. Commit 1138224e papered
 * over that with an unconditional `PLANT_ADMIN_ROLES` deny, which also beat an
 * explicit `PM_*` grant and became the reported defect. Both call sites now
 * share this helper so they cannot disagree again.
 *
 * There is deliberately no role-level deny. A plant admin carries no Priority
 * Management remit of its own — it simply holds none of these permissions — so
 * a grant made by a super manager is honoured like anyone else's. The backend
 * resolves it the same way: `access_control` authorizes when any single held
 * permission matches the route.
 *
 * `UM_user_acceptance` is absent on purpose. It grants GET on
 * `/tracking/uap/priorities` but not on `/tracking/uap/suggestions/countries`,
 * which the page requests on load, so admitting it would swap a redirect for a
 * 403.
 *
 * @param {string[]} permissionIds Permissions of the signed-in user.
 * @returns {boolean}
 */
export function mayAccessPriorityManager (permissionIds) {
  const permissions = permissionIds || []
  return PRIORITY_MANAGER_PERMISSIONS.some(
    permission => permissions.includes(permission)
  )
}
